// ============================================================================
// FSA STATEMENT PDF PREVIEW MODAL
// ============================================================================
// Fetches the server-rendered Statement preview PDF (BS / PL / CF) as a
// Blob, renders it inline in an <iframe> (browser-native PDF viewer), and
// offers Download + Open/Print. Mirrors `FsaTemplatePdfPreviewModal.tsx`
// exactly — same blob → object-URL → iframe flow — but bound to the
// Statement-scoped backend endpoint that reads
// fsa.tbl_engagement_statement_data and walks the saved layout JSON.
//
// Contract: STRICTLY saved-workspace only. If the auditor has not Saved
// the Statement, the backend returns 400 and the modal shows the error
// inline. Refresh + Save first, then re-open the modal.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { fsaStatementsService } from 'api/services/fsa/fsaStatementsService';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';

interface FsaStatementPdfPreviewModalProps {
  isOpen: boolean;
  engagementId: ID | null;
  reportId: ID | null;
  /** "BS" / "PL" / "CF" — drives the download filename. */
  reportCode?: string | null;
  /** "Balance Sheet" / "Profit & Loss" / "Cash Flow" — shown in the modal title chip. */
  reportName?: string | null;
  /** Carry the current display-mode toggle through to the backend so rounded/actual matches the screen. */
  amountMode?: 'actual' | 'rounded';
  /**
   * TRUE when the Statement has edits that haven't been saved. Never blocks
   * the preview — the auditor decides what to print — but this PDF is
   * rendered strictly from SAVED data, so an unsaved screen and this
   * document can disagree. That is exactly how an untallied Balance Sheet
   * reaches a client while the screen looked tallied, so it is stated
   * prominently rather than left to be noticed.
   */
  hasUnsavedChanges?: boolean;
  onClose: () => void;
}

const FsaStatementPdfPreviewModal: React.FC<FsaStatementPdfPreviewModalProps> = ({
  isOpen,
  engagementId,
  reportId,
  reportCode,
  reportName,
  amountMode = 'actual',
  hasUnsavedChanges = false,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    (async () => {
      if (!isOpen || !engagementId || !reportId) return;
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      try {
        const blob = await fsaStatementsService.getPreviewPdfBlob(engagementId, reportId, amountMode);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      } catch (err: any) {
        if (cancelled) return;
        // The blob channel doesn't carry the JSON envelope. Try to parse
        // the server's message from a blob-typed error response; fall
        // back to axios's generic message.
        let message: string | null = null;
        const data = err?.response?.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            message = parsed?.message ?? null;
          } catch { /* leave message null */ }
        } else if (typeof data === 'string') {
          message = data;
        } else if (data?.message) {
          message = data.message;
        }
        setError(message || err?.message || 'Failed to load preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isOpen, engagementId, reportId, amountMode]);

  const downloadName = reportCode
    ? `FSA-${reportCode}-${amountMode}.pdf`
    : reportId
    ? `FSA-Statement-${reportId}-${amountMode}.pdf`
    : 'FSA-Statement.pdf';

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    notificationService.toast.success('Download started.');
  }, [blobUrl, downloadName]);

  const handlePrint = useCallback(() => {
    if (!blobUrl) return;
    const opened = window.open(blobUrl, '_blank');
    if (!opened) {
      notificationService.toast.warning(
        'Pop-up blocked. Allow pop-ups from this site so the PDF can open in a new tab.',
        6000,
      );
    }
  }, [blobUrl]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
            <IconSystem.print size={16} />
            <h2 className="modal-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Statement Preview
              {(reportCode || reportName) && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 500 }}>
                  · {reportCode}{reportName ? ` — ${reportName}` : ''}
                </span>
              )}
              <span style={{
                marginLeft: '0.5rem', padding: '1px 8px', borderRadius: 999,
                background: '#dbeafe', color: '#1e40af',
                fontSize: '0.66rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
              }}>
                {amountMode}
              </span>
            </h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div
          className="modal-content"
          style={{
            flex: 1, padding: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            background: 'var(--bg-secondary)',
          }}
        >
          {hasUnsavedChanges && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '8px 12px', background: '#fef3c7', color: '#92400e',
              borderBottom: '1px solid #f59e0b', fontSize: '0.8rem', lineHeight: 1.4,
            }}>
              <IconSystem.warning size={14} />
              <span>
                <strong>Rendered from the last saved version.</strong> This Statement has unsaved
                changes — the figures below may differ from the screen, including whether it tallies.
                Save and preview again before issuing.
              </span>
            </div>
          )}
          {loading && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
            }}>
              <div style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                Rendering PDF…
              </span>
            </div>
          )}

          {error && !loading && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: '0.5rem', padding: '2rem', textAlign: 'center',
            }}>
              <IconSystem.alert size={28} />
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Could not load preview</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{error}</div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={`${blobUrl}#toolbar=1&navpanes=0`}
              title="FSA Statement Preview PDF"
              style={{ flex: 1, width: '100%', height: '100%', border: 'none', minHeight: '60vh' }}
            />
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button
            className="btn btn-secondary"
            onClick={handlePrint}
            disabled={loading || !blobUrl || !!error}
            title="Open in a new tab — use the browser's Print option from there"
          >
            <IconSystem.print size={16} />
            Open / Print
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={loading || !blobUrl || !!error}
          >
            <IconSystem.download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </>
  );
};

export default FsaStatementPdfPreviewModal;
